import { useProjectParams } from '@hooks/useProjectParams';
import Style from 'components/Toolbar/toolBar.module.css';
import { ActKc3LevelEnum, ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { cnMixSpace, Layout } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { ActKC3LevelSelect } from './components/ActKC3LevelSelect/ActKC3LevelSelect';
import { ActKC3ListTable } from './components/ActKC3ListTable';
import { useActKC3ListData } from './components/ActKC3ListTable/hooks';
import type { ActListItemWithGroupInfo } from './components/ActKC3ListTable/types';
import { AddOrEditActKc3Modal } from './components/AddOrEditActKc3/AddOrEditActKc3Modal';
import { useUerChangeActKC3Level } from './hooks/useUerChangeActKC3Level';

export const ActKC3List = () => {
    const { id: projectId, methodology } = useProjectParams();
    const { projectData, refreshProject } = useProjectData(projectId);
    const { aktKC3: actKC3Rules } = useAccess();

    const [levalItemToAddTo, setLevalItemToAddTo] = useState<ActListItemWithGroupInfo>();

    const tableHelpersRef = useRef<{ api: AgGridReact; resetCheckedRows: () => void }>();
    const contractUniqueId = useMemo(() => projectData?.contractUniqueId, [projectData?.contractUniqueId]);
    const contractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);

    const uerChangeActKc3Level = useUerChangeActKC3Level({ contractId: projectData?.contractId, refreshProject });

    const { refreshListActListData } = useActKC3ListData({
        contractId,
        tableApi: tableHelpersRef?.current?.api?.api,
        withEffect: false,
    });

    const currentCostRounding = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 2,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}/`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const actKC3Level: ActKc3LevelEnum | undefined = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.actKC3Level,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.actKC3Level],
    );

    const canChangeActKC3Level: boolean = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.canChangeActKC3Level,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.canChangeActKC3Level],
    );

    const isCreatingDisabled = useMemo(
        () =>
            projectData?.contractNavigation?.data?.contractNavData?.status !== ContractStatusEnum.InImplementation ||
            projectData?.status !== ProjectStatusEnum.InImplementation ||
            !actKC3Rules?.canCreate,
        [actKC3Rules?.canCreate, projectData?.contractNavigation?.data?.contractNavData?.status, projectData?.status],
    );

    const onActKc3Add = useCallback((data: ActListItemWithGroupInfo) => setLevalItemToAddTo(data), []);
    const onModalClose = useCallback(
        (hasSaved?: boolean) => {
            if (hasSaved) {
                refreshProject();
                refreshListActListData?.();
                tableHelpersRef?.current?.resetCheckedRows();
            }

            setLevalItemToAddTo(undefined);
        },
        [refreshListActListData, refreshProject],
    );

    return (
        <ProjectPageLayout
            title="Справки КС-3"
            breadCrumbsItems={breadCrumbsItems}
            headerBottom={
                <Layout
                    className={`${cnMixSpace({
                        mB: 'xl',
                        pB: 'xl',
                    })} ${Style.fullLine}`}
                >
                    <ActKC3LevelSelect
                        actKC3Level={actKC3Level}
                        onChange={uerChangeActKc3Level}
                        unableSelection={!canChangeActKC3Level}
                    />
                </Layout>
            }
        >
            <ActKC3ListTable
                ref={tableHelpersRef}
                contractId={contractId}
                costRounding={currentCostRounding}
                contractUniqueId={contractUniqueId}
                actKC3Level={actKC3Level}
                onActKc3Add={onActKc3Add}
            />
            {!!levalItemToAddTo && (
                <AddOrEditActKc3Modal
                    contractId={contractId}
                    onClose={onModalClose}
                    isReadonly={isCreatingDisabled}
                    workOrderId={actKC3Level === ActKc3LevelEnum.WorkOrder ? levalItemToAddTo.id : undefined}
                    rootContractObjectId={actKC3Level === ActKc3LevelEnum.Object ? levalItemToAddTo.id : undefined}
                />
            )}
        </ProjectPageLayout>
    );
};

ActKC3List.displayName = 'ActKC3List';
